/**
 * Image Marker Parser Utility
 *
 * Parses OMAP_IMAGE markers embedded in backend responses and converts them
 * to markdown image syntax for rendering by ReactMarkdown.
 *
 * Backend format: <!--OMAP_IMAGE|||{absolute_path}|||{title}-->
 * Output format: ![{title}]({url})
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000'

interface ImageMarker {
  fullMatch: string
  absolutePath: string
  title: string
}

/**
 * Extracts all OMAP_IMAGE markers from text
 */
function extractImageMarkers(text: string): ImageMarker[] {
  const markers: ImageMarker[] = []

  // Pattern: <!--OMAP_IMAGE|||{path}|||{title}-->
  const pattern = /<!--OMAP_IMAGE\|\|\|(.+?)\|\|\|(.+?)-->/g

  let match: RegExpExecArray | null
  while ((match = pattern.exec(text)) !== null) {
    markers.push({
      fullMatch: match[0],
      absolutePath: match[1],
      title: match[2],
    })
  }

  return markers
}

/**
 * Converts Windows absolute path to backend API static URL
 *
 * Handles two types of static files:
 * 1. assets: Pre-existing images (Metrics, Frameworks)
 *    C:\...\static\assets\Metrics\metric_compliance.jpg -> /static/assets/Metrics/metric_compliance.jpg
 * 2. visuals: Dynamically generated charts from viz.py
 *    C:\...\static\visuals\Monthly_Revenue_20260121.png -> /static/visuals/Monthly_Revenue_20260121.png
 */
function convertPathToUrl(absolutePath: string): string {
  const pathParts = absolutePath.split(/[\\\/]/)

  // Check for 'visuals' folder (dynamically generated charts, optionally under <company_id>/)
  const visualsIndex = pathParts.findIndex(part => part === 'visuals')
  if (visualsIndex !== -1 && visualsIndex < pathParts.length - 1) {
    const subpath = pathParts.slice(visualsIndex + 1).filter(Boolean).join('/')
    return `${API_BASE_URL}/static/visuals/${subpath}`
  }

  // Check for 'assets' folder (pre-existing Metrics/Frameworks images)
  const assetsIndex = pathParts.findIndex(part => part === 'assets')
  if (assetsIndex !== -1 && assetsIndex < pathParts.length - 2) {
    // Get category (Metrics/Frameworks) and filename
    const category = pathParts[assetsIndex + 1]
    const filename = pathParts[assetsIndex + 2]
    return `${API_BASE_URL}/static/assets/${category}/${filename}`
  }

  // Fallback: just use the filename under assets
  const filename = pathParts[pathParts.length - 1]
  return `${API_BASE_URL}/static/assets/${filename}`
}

/**
 * Parses text with OMAP_IMAGE markers and converts them to markdown image syntax
 *
 * @param text - Text containing <!--OMAP_IMAGE|||path|||title--> markers
 * @returns Text with markers replaced by markdown images ![title](url)
 */
export function parseImageMarkers(text: string): string {
  if (!text) return text

  const markers = extractImageMarkers(text)

  if (markers.length === 0) return text

  let processedText = text

  // Replace each marker with markdown image syntax
  for (const marker of markers) {
    const imageUrl = convertPathToUrl(marker.absolutePath)
    const markdownImage = `![${marker.title}](${imageUrl})`
    processedText = processedText.replace(marker.fullMatch, markdownImage)
  }

  return processedText
}

/**
 * Checks if text contains any OMAP_IMAGE markers
 */
export function hasImageMarkers(text: string): boolean {
  return /<!--OMAP_IMAGE\|\|\|/.test(text)
}
